/**
 * Service List Component
 * Task 37: Reusable service catalog view for service-based industries
 */

import { useState, useMemo } from 'react';
import { IndustryService } from '@/types/industry';
import { CompanyType } from '@/types/company';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { MoreHorizontal, Pencil, Trash2, Plus, Search, Eye, EyeOff } from 'lucide-react';

interface ServiceListProps {
  services: IndustryService[];
  domain: CompanyType;
  onEdit?: (service: IndustryService) => void;
  onDelete?: (service: IndustryService) => void;
  onToggleActive?: (service: IndustryService) => void;
  onCreate?: () => void;
}

const DOMAIN_TERMINOLOGY: Record<string, { singular: string; plural: string; categoryLabel: string }> = {
  [CompanyType.HEALTH_CLINICS]: {
    singular: 'Procedimento',
    plural: 'Procedimentos',
    categoryLabel: 'Especialidade',
  },
  [CompanyType.VETERINARY]: {
    singular: 'Procedimento',
    plural: 'Procedimentos',
    categoryLabel: 'Tipo',
  },
  [CompanyType.BEAUTY_CLINICS]: {
    singular: 'Procedimento',
    plural: 'Procedimentos',
    categoryLabel: 'Categoria',
  },
  [CompanyType.BEAUTY_SALONS]: {
    singular: 'Serviço',
    plural: 'Serviços',
    categoryLabel: 'Categoria',
  },
  [CompanyType.PILATES_YOGA]: {
    singular: 'Aula/Pacote',
    plural: 'Aulas/Pacotes',
    categoryLabel: 'Tipo',
  },
  [CompanyType.AUTO_REPAIR]: {
    singular: 'Serviço',
    plural: 'Serviços',
    categoryLabel: 'Categoria',
  },
};

export function ServiceList({
  services,
  domain,
  onEdit,
  onDelete,
  onToggleActive,
  onCreate,
}: ServiceListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  const terminology = DOMAIN_TERMINOLOGY[domain] || {
    singular: 'Serviço',
    plural: 'Serviços',
    categoryLabel: 'Categoria',
  };

  // Extract unique categories from services
  const categories = useMemo(() => {
    const cats = new Set<string>();
    services.forEach((service) => {
      if (service.category) cats.add(service.category);
    });
    return Array.from(cats).sort();
  }, [services]);

  // Filter services
  const filteredServices = useMemo(() => {
    return services.filter((service) => {
      // Search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchesName = service.name.toLowerCase().includes(term);
        const matchesDescription = service.description?.toLowerCase().includes(term);
        if (!matchesName && !matchesDescription) return false;
      }

      // Status filter
      if (statusFilter === 'active' && !service.is_active) return false;
      if (statusFilter === 'inactive' && service.is_active) return false;

      // Category filter
      if (categoryFilter !== 'all' && service.category !== categoryFilter) return false;

      return true;
    });
  }, [services, searchTerm, statusFilter, categoryFilter]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDuration = (minutes?: number) => {
    if (!minutes) return '-';
    if (minutes < 60) return `${minutes}min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h${mins}min` : `${hours}h`;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{terminology.plural}</CardTitle>
              <CardDescription>
                Gerencie o catálogo de {terminology.plural.toLowerCase()}
              </CardDescription>
            </div>
            {onCreate && (
              <Button onClick={onCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Novo {terminology.singular}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={`Buscar ${terminology.plural.toLowerCase()}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={(value: any) => setStatusFilter(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Status</SelectItem>
                <SelectItem value="active">Apenas Ativos</SelectItem>
                <SelectItem value="inactive">Apenas Inativos</SelectItem>
              </SelectContent>
            </Select>

            {/* Category Filter */}
            {categories.length > 0 && (
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    Todas as {terminology.categoryLabel}s
                  </SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Results Count */}
          <div className="mt-4 text-sm text-muted-foreground">
            Mostrando {filteredServices.length} de {services.length} {terminology.plural.toLowerCase()}
          </div>
        </CardContent>
      </Card>

      {/* Service Table */}
      <Card>
        <CardContent className="pt-6">
          {filteredServices.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              {searchTerm || statusFilter !== 'all' || categoryFilter !== 'all'
                ? 'Nenhum serviço encontrado com os filtros selecionados'
                : `Nenhum ${terminology.singular.toLowerCase()} cadastrado`}
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>{terminology.categoryLabel}</TableHead>
                    <TableHead>Preço</TableHead>
                    <TableHead>Duração</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredServices.map((service) => (
                    <TableRow key={service.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{service.name}</div>
                          {service.description && (
                            <div className="text-sm text-muted-foreground line-clamp-1">
                              {service.description}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {service.category ? (
                          <Badge variant="outline">{service.category}</Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatCurrency(service.price)}
                      </TableCell>
                      <TableCell>{formatDuration(service.duration)}</TableCell>
                      <TableCell>
                        <Badge variant={service.is_active ? 'default' : 'secondary'}>
                          {service.is_active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {onEdit && (
                              <DropdownMenuItem onClick={() => onEdit(service)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Editar
                              </DropdownMenuItem>
                            )}
                            {onToggleActive && (
                              <DropdownMenuItem onClick={() => onToggleActive(service)}>
                                {service.is_active ? (
                                  <>
                                    <EyeOff className="mr-2 h-4 w-4" />
                                    Desativar
                                  </>
                                ) : (
                                  <>
                                    <Eye className="mr-2 h-4 w-4" />
                                    Ativar
                                  </>
                                )}
                              </DropdownMenuItem>
                            )}
                            {onDelete && (
                              <DropdownMenuItem
                                onClick={() => onDelete(service)}
                                className="text-destructive"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Excluir
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
